import React, { useMemo } from 'react';
import DataBadge, { DataTier } from './DataBadge';

type MaybeDate = string | Date | null | undefined;

const toDate = (value: MaybeDate): Date | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isFinite(date.getTime()) ? date : null;
};

const formatAge = (seconds: number) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '—';
  if (seconds < 1) return '0s';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = seconds / 60;
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const hours = minutes / 60;
  return `${hours.toFixed(hours < 10 ? 1 : 0)}h`;
};

const formatValue = (value: unknown) => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return '—';
    return value % 1 === 0 ? value.toString() : value.toFixed(2);
  }
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

export interface DataProvenanceRowProps {
  tier: DataTier;
  source?: string;
  asOf?: MaybeDate;
  generatedAt?: MaybeDate;
  refresh?: string;
  inputs?: Record<string, unknown> | null;
  warnings?: string[] | null;
  className?: string;
  compact?: boolean;
}

const DataProvenanceRow: React.FC<DataProvenanceRowProps> = ({
  tier,
  source,
  asOf,
  generatedAt,
  refresh,
  inputs,
  warnings,
  className = '',
  compact = false,
}) => {
  const asOfDate = toDate(asOf) || toDate(generatedAt);
  const now = Date.now();
  const ageSeconds = asOfDate ? Math.max(0, (now - asOfDate.getTime()) / 1000) : null;

  const inputSummary = useMemo(() => {
    if (!inputs) return [];
    const keyOrder = ['load_factor', 'island', 'solver_type', 'schedule', 'run_id', 'scenario'];
    const used = new Set<string>();
    const parts: Array<{ key: string; value: unknown }> = [];

    for (const key of keyOrder) {
      if (Object.prototype.hasOwnProperty.call(inputs, key)) {
        parts.push({ key, value: (inputs as any)[key] });
        used.add(key);
      }
    }

    // Add up to 2 more keys to avoid huge rows.
    for (const [key, value] of Object.entries(inputs)) {
      if (used.has(key)) continue;
      parts.push({ key, value });
      if (parts.length >= keyOrder.length + 2) break;
    }

    return parts;
  }, [inputs]);

  const warningsText = (warnings || []).filter(Boolean).join(' · ');

  return (
    <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-xs ${className}`}>
      <DataBadge tier={tier} />
      {source && <span className="text-gray-300">Source: {source}</span>}
      {asOfDate && (
        <span className="text-gray-400">
          As-of <span className="text-gray-300">{asOfDate.toLocaleTimeString()}</span>
          {ageSeconds !== null ? ` · age ${formatAge(ageSeconds)}` : ''}
        </span>
      )}
      {!asOfDate && <span className="text-gray-500">As-of: unknown</span>}
      {refresh && <span className="text-gray-500">Refresh: {refresh}</span>}

      {!compact && inputSummary.length > 0 && (
        <span className="text-gray-500">
          Inputs:{' '}
          {inputSummary.map((kv, idx) => (
            <span key={kv.key} className="text-gray-400">
              {idx > 0 ? ' · ' : ''}
              {kv.key}={formatValue(kv.value)}
            </span>
          ))}
        </span>
      )}

      {warningsText && (
        <span className="text-amber-300">
          {compact ? '!' : 'Warnings:'} {warningsText}
        </span>
      )}
    </div>
  );
};

export default DataProvenanceRow;

